import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { apiDetail, useCreateScenarioRetireRequest } from "../../utils/portfolio-risk-concentration/api/scenario";

/**
 * Ask for an approved scenario to be retired (the trash icon on its card). Nothing is deleted: this raises a request, a contributor approves
 * or rejects it with notes, and only an approval marks the scenario Retired. The request and the decision stay in the log either way.
 */
export function ScenarioRetireDialog({
  open,
  onOpenChange,
  scenarioRef,
  label,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenarioRef: string;
  /** How the scenario reads on its card, e.g. "Oct 8 · Code 50 · CAP2". */
  label: string;
}) {
  const create = useCreateScenarioRetireRequest();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  async function submit() {
    if (!reason.trim()) {
      setError("A reason is required.");
      return;
    }
    try {
      await create.mutateAsync({ scenarioRef, reason: reason.trim() });
      onOpenChange(false);
    } catch (err) {
      setError(apiDetail(err, "Could not raise the request."));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Retire scenario</DialogTitle>
          <DialogDescription>
            {label}. This does not delete anything: the request goes to an approver, and if approved the scenario is marked Retired
            (every table keeps it, and the log keeps the decision).
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="retire-reason">Reason</Label>
          <Textarea
            id="retire-reason"
            className="mt-1"
            rows={3}
            placeholder="Why should this scenario be retired?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Request retirement
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}
